import { KuiModalRefComponent } from 'app/key-ui/modal/modal-ref.component';
import { Component, OnInit } from '@angular/core';
import { IKuiModalAction } from 'app/key-ui';
import { AppService } from 'app/app.service';
import { DashboardWidget } from '@key-telematics/fleet-api-client';
import { DashboardsService } from 'app/services';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';
import { HistoryService, HistoryChangeEvent } from 'app/services/history/history.service';

export interface EditDashboardModalOptions {
    widgets: { [id: string]: DashboardWidget };
    title: string;
    id: string;
    useSampleData: boolean;
    isDashboardTemplate: boolean;
}

@Component({
    templateUrl: 'dashboard-modal.component.html',
    styleUrls: ['dashboard-modal.component.scss'],
    providers: [HistoryService],
    standalone: false
})
export class EditDashboardModalComponent extends KuiModalRefComponent<EditDashboardModalOptions> implements OnInit {
    modalActions: IKuiModalAction[];
    loading = true;
    widgets: { [id: string]: DashboardWidget };
    errorMessage: string;
    skipPlacementUpdate: boolean;

    constructor(
        private i18n: TranslateService,
        public app: AppService,
        public dashboardsService: DashboardsService,
        public history: HistoryService<{ [id: string]: DashboardWidget }>
    ) {
        super();
    }

    ngOnInit() {
        this.on(this.history.onChange$, (event: HistoryChangeEvent<{ [id: string]: DashboardWidget }>) => {
            const { canUndo, currentValue, type } = event;
            this.updateModalActions(canUndo);
            if (type === 'undo') {
                this.widgets = cloneDeep({
                    ...this.widgets,
                    ...currentValue,
                });
            }
        });

        this.loading = false;
        this.widgets = cloneDeep(this.data.widgets);
        this.history.init(this.widgets); // add initial values to history queue
        this.updateModalActions(false);
    }

    detachModal() {
        this.actions.close();
    }

    updateWidget({ id, update }: { id: string, update: DashboardWidget }) {
        this.widgets[id] = {
            ...this.widgets[id],
            ...update,
        };

        this.history.add(this.widgets);
    }

    updateWidgetsPlacement(updates: { [id: string]: DashboardWidget }) {
        Object.keys(updates).forEach(id => {
            this.widgets[id] = {
                ...this.widgets[id],
                ...updates[id],
            };
        });

        if (!this.skipPlacementUpdate) {
            this.history.add(this.widgets);
        }

        this.skipPlacementUpdate = false;
    }

    removeWidget(id: string) {
        this.widgets[id] = null;
        this.history.add(this.widgets);
        // we need to skip the subsequent updatePlacement event as it is a direct result of the deletion
        // and when you click undo you'd expect the deleted widget just to come back
        this.skipPlacementUpdate = true;
    }

    updateModalActions(undo: boolean) {
        this.modalActions = [{
            icon: 'undo',
            disabled: !undo,
            style: 'secondary',
            action: () => {
                this.history.undo();
                return true;
            },
        }, {
            text: this.i18n.instant('DIALOG.CANCEL'),
            style: 'secondary',
            action: () => this.actions.close(),
        },
        {
            text: this.i18n.instant('DIALOG.SAVE'),
            disabled: !undo,
            style: 'primary',
            action: () => {
                this.loading = true;
                this.actions.save(this.widgets);
                return true;
            },
        }];
    }
}
